import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { factorService, scenarioService, narrativeService } from '../services/api';

import CausalFactorsStep from './wizard/CausalFactorsStep';
import DriverTestStep from './wizard/DriverTestStep';
import ExtremeValuesStep from './wizard/ExtremeValuesStep';
import ScenariosStep from './wizard/ScenariosStep';
import SecondaryFactorsStep from './wizard/SecondaryFactorsStep';
import NarrativesStep from './wizard/NarrativesStep';

const ProjectWizard = ({ project, onProjectUpdate, collaboration, onFinish }) => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [factors, setFactors] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [ambNarrativa, setAmbNarrativa] = useState([]);
  const [loading, setLoading] = useState(false);
  const [collapsedSteps, setCollapsedSteps] = useState(new Set());

  const steps = [
    {
      id: 1,
      name: t('wizard.step1'),
      shortName: t('wizard.step1Short'),
      description: t('wizard.step1Description'),
      component: CausalFactorsStep,
      stats: () => ({ total: factors.length, completed: factors.length })
    },
    {
      id: 2,
      name: t('wizard.step2'),
      shortName: t('wizard.step2Short'),
      description: t('wizard.step2Description'),
      component: DriverTestStep,
      stats: () => ({ 
        total: factors.length, 
        completed: factors.filter(f => f.certainty_test && f.significance_test && f.independence_test).length 
      })
    },
    {
      id: 3,
      name: t('wizard.step3'),
      shortName: t('wizard.step3Short'),
      description: t('wizard.step3Description'),
      component: ExtremeValuesStep,
      stats: () => ({ 
        total: drivers.length, 
        completed: drivers.filter(d => d.extreme_value_low && d.extreme_value_high).length 
      })
    },
    {
      id: 4,
      name: t('wizard.step4'),
      shortName: t('wizard.step4Short'),
      description: t('wizard.step4Description'),
      component: ScenariosStep,
      stats: () => ({ 
        total: scenarios.length, 
        completed: scenarios.filter(s => s.is_selected).length 
      })
    },
    {
      id: 5,
      name: t('wizard.step5'),
      shortName: t('wizard.step5Short'),
      description: t('wizard.step5Description'),
      component: SecondaryFactorsStep,
      // `completed` estava clavat a 0: la passa 5 no comptava mai res i no
      // podia arribar a donar-se per acabada, cosa que a més falsejava el
      // percentatge global de dalt. Ara compta els que ja tenen assignació.
      stats: () => {
        const secundaris = factors.filter(f => !f.is_driver);
        return {
          total: secundaris.length,
          completed: secundaris.filter(f => f.secondary_factor_assignment).length
        };
      }
    },
    {
      id: 6,
      name: t('wizard.step6'),
      shortName: t('wizard.step6Short'),
      description: t('wizard.step6Description'),
      component: NarrativesStep,
      // Igual que la passa 5, `completed` estava clavat a 0 i la passa no
      // podia donar-se mai per acabada.
      stats: () => ({
        total: scenarios.filter(s => s.is_selected).length,
        completed: ambNarrativa.length
      })
    }
  ];

  useEffect(() => {
    fetchFactors();
    fetchDrivers();
    fetchScenarios();
  }, [project.id]);

  // Handle collaboration editing notifications
  useEffect(() => {
    if (collaboration && collaboration.isConnected && project?.workspace_id && currentStep) {
      collaboration.startEditing(`step_${currentStep}`);
      
      // Cleanup function to stop editing when component unmounts or step changes
      return () => {
        collaboration.stopEditing(`step_${currentStep}`);
      };
    }
  }, [collaboration?.isConnected, project?.workspace_id, currentStep]); // Remove collaboration object dependency

  /* Els canvis dels companys, a les sis passes.
   *
   * Fins a l'11/08/2026 aquestes passes **no rebien res**: el mapeig, les notes
   * i el backcasting es veien en directe i les sis passes del mètode no, o
   * sigui que dos que treballaven alhora a la passa 1 no es veien els factors
   * fins a recarregar. Ara el servidor també hi emet (vegeu
   * `backend/utils/realtime.py`).
   *
   * Aquí es torna a demanar la secció en comptes d'aplicar cada objecte a mà,
   * com fa el mapeig. És a posta: aquestes passes són llistes curtes amb
   * botons i seleccions, no un llenç on cadascú arrossega la seva caixa, i
   * tornar-les a demanar és molt menys codi per mantenir. Els camps de text
   * (`CampText`) ignoren el que arriba de fora mentre els tens enfocats, o
   * sigui que no et poden saltar les lletres mentre escrius.
   *
   * Va amb retard: generar escenaris n'emet un de sol, però assignar factors
   * secundaris o corregir tres factors seguits en fa arribar uns quants de
   * cop, i sense això es demanaria la secció una vegada per cadascun.
   */
  const temporitzador = useRef(null);
  useEffect(() => {
    const ENTITATS = ['factor', 'driver', 'scenario', 'narrative'];
    const aplica = (event) => {
      const { entity, user } = event.detail || {};
      if (!ENTITATS.includes(entity)) return;
      // L'eco del que has fet tu s'ignora: ja tens la teva pròpia resposta.
      if (!user?.user_id || !currentUser?.id || user.user_id === currentUser.id) return;
      clearTimeout(temporitzador.current);
      temporitzador.current = setTimeout(() => {
        fetchFactors();
        fetchDrivers();
        fetchScenarios();
      }, 500);
    };
    window.addEventListener('collaboration-entity-change', aplica);
    return () => {
      window.removeEventListener('collaboration-entity-change', aplica);
      clearTimeout(temporitzador.current);
    };
  }, [currentUser?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchFactors = async () => {
    try {
      // Check if this is a case (has workspace_id) or a project
      const data = project.workspace_id 
        ? await factorService.getCaseFactors(project.id)
        : await factorService.getProjectFactors(project.id);
      setFactors(data);
    } catch (error) {
      console.error('Error fetching factors:', error);
    }
  };

  const fetchDrivers = async () => {
    try {
      // Check if this is a case (has workspace_id) or a project
      const data = project.workspace_id 
        ? await factorService.getCaseDrivers(project.id)
        : await factorService.getProjectDrivers(project.id);
      setDrivers(data);
    } catch (error) {
      console.error('Error fetching drivers:', error);
    }
  };

  // Quins escenaris seleccionats ja tenen narrativa. Són poques peticions —una
  // per escenari triat— i és l'única manera de saber-ho: no hi ha cap endpoint
  // que ho digui de cop.
  const fetchNarratives = async (llista) => {
    const triats = (llista || []).filter(s => s.is_selected);
    if (triats.length === 0) { setAmbNarrativa([]); return; }
    try {
      const resultats = await Promise.all(triats.map(async (s) => {
        try {
          const n = await narrativeService.getScenarioNarratives(s.id);
          const teContingut = Array.isArray(n) ? n.length > 0 : !!n;
          return teContingut ? s.id : null;
        } catch { return null; }
      }));
      setAmbNarrativa(resultats.filter(Boolean));
    } catch (error) {
      console.error('Error fetching narratives:', error);
    }
  };

  const fetchScenarios = async () => {
    try {
      // Check if this is a case (has workspace_id) or a project
      const data = project.workspace_id 
        ? await scenarioService.getCaseScenarios(project.id)
        : await scenarioService.getProjectScenarios(project.id);
      setScenarios(data);
      fetchNarratives(data);
    } catch (error) {
      console.error('Error fetching scenarios:', error);
    }
  };

  const esDarreraPassa = currentStep === steps.length;

  // Es pot acabar quan tots els escenaris triats tenen narrativa. Si no n'has
  // triat cap, encara no hi ha res a acabar.
  const escenarisTriats = scenarios.filter(s => s.is_selected);
  const potAcabar = !!onFinish
    && escenarisTriats.length > 0
    && escenarisTriats.every(s => ambNarrativa.includes(s.id));

  const canProceedToStep = (stepId) => {
    switch (stepId) {
      case 1:
        return true;
      case 2:
        return factors.length > 0;
      case 3:
        return factors.some(f => f.is_driver);
      case 4:
        return drivers.length > 0 && drivers.every(d => d.extreme_value_low && d.extreme_value_high);
      case 5:
        return scenarios.length > 0;
      case 6:
        return scenarios.some(s => s.is_selected);
      default:
        return false;
    }
  };

  const getStepStatus = (stepId) => {
    if (stepId < currentStep) return 'completed';
    if (stepId === currentStep) return 'current';
    if (canProceedToStep(stepId)) return 'available';
    return 'locked';
  };

  const handleStepClick = (stepId) => {
    if (stepId <= currentStep || canProceedToStep(stepId)) {
      // Stop editing previous step if using collaboration
      if (collaboration && collaboration.isConnected && project?.workspace_id && currentStep) {
        collaboration.stopEditing(`step_${currentStep}`);
      }
      
      setCurrentStep(stepId);
      
      // Start editing new step if using collaboration
      if (collaboration && collaboration.isConnected && project?.workspace_id) {
        collaboration.startEditing(`step_${stepId}`);
      }
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length && canProceedToStep(currentStep + 1)) {
      // Stop editing current step
      if (collaboration && collaboration.isConnected && project?.workspace_id) {
        collaboration.stopEditing(`step_${currentStep}`);
      }
      
      setCurrentStep(currentStep + 1);
      
      // Start editing next step
      if (collaboration && collaboration.isConnected && project?.workspace_id) {
        collaboration.startEditing(`step_${currentStep + 1}`);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      // Stop editing current step
      if (collaboration && collaboration.isConnected && project?.workspace_id) {
        collaboration.stopEditing(`step_${currentStep}`);
      }
      
      setCurrentStep(currentStep - 1);
      
      // Start editing previous step
      if (collaboration && collaboration.isConnected && project?.workspace_id) {
        collaboration.startEditing(`step_${currentStep - 1}`);
      }
    }
  };

  const refreshData = () => {
    fetchFactors();
    fetchDrivers();
    fetchScenarios();
  };

  const currentStepData = steps[currentStep - 1];
  const CurrentStepComponent = currentStepData.component;

  // Aquí hi havia `getStepClasses`, una taula de cinc colors per a cada estat
  // de cada passa. Va morir amb la filera de rodones: la barra nova no
  // necessita cinc classes per estat, en necessita tres, i les té a dins.


  return (
    <div className="space-y-6">
      {/* Selector de passes.

          És el mateix que el del backcasting: una barra amb el nom de cada
          passa, no vuit rodones numerades a la dreta d'un títol. Les rodones
          soles obliguen a passar el ratolí per sobre per saber què hi ha a
          cadascuna, i el nom de la passa on ets quedava lluny de la fila que
          l'havia de situar.

          **Diferència amb el del backcasting**: allà la navegació és lliure i
          aquí no. El mètode és una seqüència —no pots triar valors extrems
          abans de saber quins factors són drivers— i les passes que encara no
          toquen queden apagades i sense clicar. L'`aria-disabled` i el `title`
          ho diuen a qui no ho pot veure. */}
      <nav className="flex overflow-x-auto rounded-lg border border-neutral-200 bg-neutral-50">
        {steps.map((step) => {
          const status = getStepStatus(step.id);
          const bloquejada = status === 'locked';
          const actual = status === 'current';
          const feta = status === 'completed';

          return (
            <button
              key={step.id}
              type="button"
              onClick={() => !bloquejada && handleStepClick(step.id)}
              disabled={bloquejada}
              aria-current={actual ? 'step' : undefined}
              title={bloquejada ? t('wizard.stepLocked') : step.name}
              className={`flex min-w-[9rem] flex-1 items-center gap-2 border-r border-neutral-200 px-3 py-2.5 text-left text-xs transition-colors last:border-r-0 ${
                actual
                  ? 'bg-white font-semibold text-neutral-900'
                  : bloquejada
                  ? 'cursor-not-allowed text-neutral-400'
                  : 'text-neutral-500 hover:bg-white/60'
              }`}
            >
              <span className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border font-mono text-[10px] font-bold ${
                actual
                  ? 'border-accent-600 bg-accent-600 text-white'
                  : feta ? 'border-accent-500 text-accent-600' : 'border-neutral-300 text-neutral-400'
              }`}>
                {step.id}
              </span>
              <span className="truncate">{step.shortName || step.name}</span>
            </button>
          );
        })}
      </nav>

      {/* Main Content Area */}
      <div className="bg-white rounded-lg border border-neutral-200">
        {/* Step Content */}
        <div className="p-8">
          <CurrentStepComponent
            project={project}
            factors={factors}
            drivers={drivers}
            scenarios={scenarios}
            onDataChange={refreshData}
            scenariosAmbNarrativa={ambNarrativa}
            onNext={handleNext}
            onPrevious={handlePrevious}
            canProceed={currentStep < steps.length && canProceedToStep(currentStep + 1)}
            canGoBack={currentStep > 1}
          />
        </div>

        {/* Navigation Footer */}
        <div className="px-8 py-6 flex items-center justify-between border-t border-neutral-200 bg-neutral-50 rounded-b-lg">
          <button
            onClick={handlePrevious}
            disabled={currentStep === 1}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-neutral-600 hover:text-neutral-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            {t('common.previous')}
          </button>
          
          {/* Aquí hi ha hagut, per ordre: una filera de punts que repetia el
              que la barra de dalt ja diu, un «Necessites ajuda?» sense cap
              `onClick`, i un percentatge de passes fetes. Els tres fora. El
              percentatge era el pitjor: convertia el mètode en una barra de
              progrés, com si l'objectiu fos arribar al 100 % i no pensar. La
              barra de dalt ja diu on ets i què has tocat. */}
          <span className="text-xs text-neutral-500">
            {t('common.step', { current: currentStep, total: steps.length })}
          </span>

          {/* El botó d'acabar
              ------------------
              Abans deia `disabled={currentStep === steps.length || …}`, i com
              que l'etiqueta passa a «Acaba» EXACTAMENT quan
              `currentStep === steps.length`, el botó es deia «Acaba» just en
              l'únic moment en què estava bloquejat: no es podia activar mai,
              per construcció. I al darrere no hi havia res.
              Ara, a l'última passa, s'activa quan tots els escenaris triats
              tenen narrativa i porta al resum del cas, que és on es veu la
              feina acabada. */}
          <button
            onClick={esDarreraPassa ? onFinish : handleNext}
            disabled={esDarreraPassa
              ? !potAcabar
              : !canProceedToStep(currentStep + 1)}
            title={esDarreraPassa && !potAcabar ? t('wizard.finishBlocked') : undefined}
            className="inline-flex items-center gap-2 px-6 py-2 text-sm font-medium rounded-lg text-white bg-accent-600 hover:bg-accent-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {esDarreraPassa ? t('common.complete') : t('common.next')}
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectWizard;